import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, InputAdornment, Pagination, TextField, Typography } from "@mui/material";

import { SearchIcon } from "../../../../../assets/icons/MUI/LayoutIcons";
import { UserInfo } from "../../../../../types";
import { useGroupUsersList } from "../../../../../api/queries/groups/useGroupsQueries";

import SearchUserCard from "./SearchUserCard";

const AddMembersToGroup = ({
  membersList,
  setMembersList,
  handleRemoveUser,
}: {
  membersList: UserInfo[];
  setMembersList: Dispatch<SetStateAction<UserInfo[]>>;
  handleRemoveUser: (user: UserInfo | null) => void;
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [searchText, setSearchText] = useState<string>("");
  const [offset, setOffset] = useState<number>(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  useEffect(() => {
    setSearchText(debouncedValue);
  }, [debouncedValue]);

  const { usersList, iseUsersListLoading, isUsersListHasError } = useGroupUsersList({ offset, searchText });
  const items = usersList?.items || [];
  const meta = usersList?.meta;

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => setOffset(value - 1);

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Typography
        variant="body16Bold"
        sx={{
          display: "block",
          marginBottom: "16px",
        }}
      >
        {t("pages.entity.groups.setGroupMembers")}
      </Typography>
      <TextField
        value={inputValue}
        onChange={(event) => setInputValue(event.target.value)}
        placeholder={t("pages.entity.groups.searchByName")}
        inputProps={{
          maxLength: 100,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon style={{ fill: "none", width: "24px", height: "24px" }} />
            </InputAdornment>
          ),
        }}
        variant="outlined"
        sx={{
          alignSelf: "start",
          width: "100%",
          marginBottom: "24px",
          "& .MuiOutlinedInput-root": {
            padding: "0 20px",
          },
        }}
      />
      <Typography
        variant="body13Bold"
        sx={{
          display: "block",
          marginBottom: "16px",
        }}
      >
        {t("pages.entity.groups.setGroupMembers")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          rowGap: "16px",
          marginBottom: "30px",
        }}
      >
        {items?.length > 0 &&
          items.map((user: UserInfo, index: number) => (
            <SearchUserCard
              key={user.id}
              user={user}
              isLast={10 === index + 1}
              membersList={membersList}
              setMembersList={setMembersList}
              handleRemoveUser={handleRemoveUser}
            />
          ))}
      </Box>
      {items?.length > 0 && !iseUsersListLoading && !isUsersListHasError && (
        <Pagination
          count={meta?.totalPages}
          page={!meta?.totalItems || meta?.totalItems <= 0 ? 1 : offset + 1}
          onChange={handleChangePage}
          sx={{
            marginBottom: "45px",
            direction: "ltr !important",
            "& .MuiButtonBase-root": {
              borderRadius: "8px !important",
            },
            "& .MuiPagination-ul": {
              justifyContent: "center",
            },
          }}
        />
      )}
    </Box>
  );
};

export default AddMembersToGroup;
